/**
 * The hour chart in plain words, read before the bars: the day's best hours,
 * how the country's window (and this container's own hours) rank against
 * them, exactly which extra drops the optimizer adds (the ⚡ line, computed
 * by the planner's own placement) — and, when the best hours lie
 * outside, what to change. A master config has no hours of its own: it reads
 * against the window (user, 12/10/2026). The window is a hard limit (user,
 * 12/10/2026): no drop goes outside it, whatever a container's start.
 * Hours read Danish first, the country's beside them (QA 18/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Clock, Lightbulb, Sunrise, Trophy } from 'lucide-react';

import { dkLabel, windowText } from '../../../../../utils/clock/dk-label';
import { useCountryClock, type CountryClock } from '../../../../shared/time/country-clock-context';
import { type CountryWindow, type HourView } from '../hour-chart-math';

interface Props {
  rows: HourView[];
  rankedHours: number;
  startMin: number | null;
  endMin: number | null;
  /** The country's window; null = unknown. */
  window?: CountryWindow | null;
  /** The ⚡ line — what the optimizer adds, under its hours. */
  extras?: ReactNode;
}

const hh = (h: number) => `${String(h).padStart(2, '0')}:00`;
const list = (xs: string[]) => (xs.length <= 1 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const byRank = (rows: HourView[]) => rows.filter((r) => r.rank !== null).sort((a, b) => a.rank! - b.rank!);
/** "11:00, 05:00 and 04:00 🇩🇰 · 🇦🇺 20:00, 14:00 and 13:00". */
function hoursText(rows: HourView[], c: CountryClock): string {
  const local = list(rows.map((r) => hh(r.hour)));
  const dk = rows.map((r) => dkLabel(r.hour * 60, c.tz, c.runDate));
  return dk.every(Boolean) && c.tz ? `${list(dk as string[])} 🇩🇰 · ${c.flag} ${local}` : local;
}
const isAre = (rows: HourView[], c: CountryClock) => `${hoursText(rows, c)} ${rows.length === 1 ? 'is' : 'are'}`;

const Line: FC<{ icon: typeof Trophy; tone: string; children: ReactNode }> = ({ icon: Icon, tone, children }) => (
  <p className="flex items-start gap-2 text-[13px] leading-snug">
    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} aria-hidden />
    <span>{children}</span>
  </p>
);

const WindowLine: FC<{ rows: HourView[]; best: HourView[]; window: CountryWindow; rankedHours: number }> = ({ rows, best, window: w, rankedHours }) => {
  const clock = useCountryClock();
  const inside = byRank(rows.filter((r) => r.inWindow)).slice(0, 3);
  const allBestInside = best.every((r) => r.inWindow);
  return (
    <Line icon={Sunrise} tone="text-primary">
      The country&apos;s window is <b className="font-mono">{windowText(w.startMin, w.endMin, clock)}</b>
      {allBestInside ? (
        <> — the day&apos;s best hours are all inside it.</>
      ) : inside.length > 0 ? (
        <> — its best hours: <b className="font-mono">{hoursText(inside, clock)}</b> (<b>{list(inside.map((r) => `#${r.rank}`))}</b> of {rankedHours}).</>
      ) : (
        <> — every hour of it is excluded (not judged yet).</>
      )}
    </Line>
  );
};

export const HourInsight: FC<Props> = ({ rows, rankedHours, startMin, endMin, window: w, extras }) => {
  const clock = useCountryClock();
  if (rankedHours === 0) return null;
  const best = byRank(rows).slice(0, 3);
  const ownRanked = byRank(rows.filter((r) => r.lit));
  const hasContainer = startMin !== null && endMin !== null;
  const outsideOwn = best.filter((r) => !r.inSpan && r.inWindow);
  const outsideWindow = best.filter((r) => !r.inWindow);
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
      <Line icon={Trophy} tone="text-warning">
        The best hours of the day: <b className="font-mono">{hoursText(best, clock)}</b>
      </Line>
      {w ? <WindowLine rows={rows} best={best} window={w} rankedHours={rankedHours} /> : null}
      {hasContainer ? (
        <Line icon={Clock} tone="text-primary">
          This container runs <b className="font-mono">{windowText(startMin % 1440, endMin % 1440, clock)}</b>
          {ownRanked.length > 0 ? (
            <>
              {' '}— those hours rank <b>{list(ownRanked.map((r) => `#${r.rank}`))}</b> of {rankedHours}.
            </>
          ) : (
            <> — no hour of it is both ranked and inside the window.</>
          )}
        </Line>
      ) : null}
      {hasContainer ? extras : null}
      {hasContainer && outsideOwn.length > 0 && rows.some((r) => r.inSpan) ? (
        <Line icon={Lightbulb} tone="text-success">
          {outsideOwn.length === best.length ? 'The best hours are' : isAre(outsideOwn, clock)} outside its hours — no drop goes there. To use them, move its start in Schedule.
        </Line>
      ) : null}
      {w && outsideWindow.length > 0 ? (
        <Line icon={Lightbulb} tone="text-success">
          {isAre(outsideWindow, clock)} outside the window — no drop goes there, the window is a hard limit. To use{' '}
          {outsideWindow.length === 1 ? 'it' : 'them'}, widen the window from its tile at the top of the country page.
        </Line>
      ) : null}
    </div>
  );
};
